import { ReactNode } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Menu, { useMenuContext } from 'components/shared/menu';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { DemographicConflict } from './demographicConflicts';

// A Patient Demographics field the packet disagrees with (OLIO-3252, Max's design): the field is
// highlighted, and clicking it shows what the packet said beside the value in use, with Dismiss and
// "Use this value". Built on the shared Menu — click to open, closes on an outside click, portalled
// so the card's overflow can't clip it. The hover-only Tooltip can't hold buttons.
//
// Without edit access (or on a closed referral) the same panel opens read-only: seeing that the
// packet disagrees is still worth knowing, even for someone who can't act on it.

interface Props {
  conflict: DemographicConflict;
  // The value in use and the packet's reading, already rendered the way the card shows them (a DOB
  // as a date; an SSN through its reveal control, since two masked numbers can share a last four).
  current: ReactNode;
  packet: ReactNode;
  // Where the packet said its reading, as citation badges; absent when nothing was recorded.
  packetSources?: ReactNode;
  canResolve: boolean;
  saving: boolean;
  onUse: (conflict: DemographicConflict) => void;
  onDismiss: (conflict: DemographicConflict) => void;
  // The card's own rendering of the value in use — what sits inside the highlight.
  children: ReactNode;
}

export default function DemographicConflictPopover({ children, ...props }: Props) {
  return (
    <Menu>
      <Menu.Trigger>
        <Highlight type='button' data-testid={`conflict-${props.conflict.key}`} aria-haspopup='dialog'>
          {children}
        </Highlight>
      </Menu.Trigger>
      <Menu.Content style={{ marginTop: 4 }}>
        <Panel {...props} />
      </Menu.Content>
    </Menu>
  );
}

// Its own component so it can reach the menu's context and close it once an answer is sent.
function Panel({
  conflict,
  current,
  packet,
  packetSources,
  canResolve,
  saving,
  onUse,
  onDismiss,
}: Omit<Props, 'children'>) {
  const { toggle } = useMenuContext();
  const answer = (respond: (conflict: DemographicConflict) => void) => () => {
    respond(conflict);
    toggle(false);
  };

  // The shared Menu closes on any document click outside its TRIGGER, and this panel is portalled
  // away from the trigger — so a click inside it (the SSN's reveal eye) would close it. Stopping the
  // click here keeps the panel open for its own controls; the two answers close it themselves.
  return (
    <Body data-testid={`conflict-panel-${conflict.key}`} onClick={(event) => event.stopPropagation()}>
      <Reading>
        <Caption>Current</Caption>
        <BodySmall as='div'>{current}</BodySmall>
      </Reading>
      <Reading>
        <Caption>Newly Extracted</Caption>
        <Valued>
          <BodySmall as='div' data-testid='conflict-packet-value'>
            {packet}
          </BodySmall>
          {packetSources}
        </Valued>
      </Reading>
      {canResolve && (
        <Buttons>
          <Button size='xsmall' variant='outline-gray' disabled={saving} onClick={answer(onDismiss)}>
            Dismiss
          </Button>
          <Button size='xsmall' disabled={saving} onClick={answer(onUse)}>
            Use New Value
          </Button>
        </Buttons>
      )}
    </Body>
  );
}

// Amber like the other "needs a look" marks on this page (FacilityMappingReview's unresolved box);
// text stays black on it, since yellow100 text fails contrast.
const Highlight = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: block;
  margin: -2px -6px;
  padding: 1px 5px;
  border: 1px solid ${colors.yellow100};
  border-radius: 4px;
  background: ${colors.yellow5};

  &:hover,
  &:focus-visible {
    background: ${colors.yellow10};
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  min-width: 200px;
`;

const Reading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Valued = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Caption = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 4px;

  button {
    padding: 4px 16px;
  }
`;
